// Vendor imports
const classNames = require('classnames')
const EmojiMart = require('emoji-mart')
const PropTypes = require('prop-types')
const React = require('react')
const { MentionsInput, Mention } = require('react-mentions')
const ReactPopper = require('react-popper')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const morePropTypes = require('client-v2/common/utils/prop-types')
const { CircleButton } = require('client-v2/common/components/buttons')

const bem = BemEntity('EmojiMentionsInput')

function EmojiMentionsInput(props) {
  const {
    className,
    children,
    disabled,
    popperPlacement,
    value,
    onChange,
    ...mentionsInputProps
  } = props
  const suggestionsRef = React.useRef(null)
  const onMentionsInputChange = (event, newValue, newPlainTextValue) => {
    const replacedPlainTextValue = replaceEmojiMarkup(newPlainTextValue)
    onChange(event, newValue, replacedPlainTextValue)
  }
  return (
    tag('div', {
      className: classNames(
        className,
        bem.block()
      )
    },
      tag(ReactPopper.Manager,
        tag(ReactPopper.Reference,
          ({ ref }) => (
            tag(MentionsInput, {
              ...mentionsInputProps,
              className: bem.element('mentions-input'),
              disabled,
              inputRef: ref,
              onChange: onMentionsInputChange,
              suggestionsPortalHost: suggestionsRef.current,
              value
            },
              tag(Mention, {
                appendSpaceOnAdd: true,
                data: queryEmojiMentionData,
                renderSuggestion: renderEmojiSuggestion,
                trigger: ':'
              }),
              children
            )
          )
        ),
        tag(ReactPopper.Popper, {
          placement: popperPlacement
        },
          ({ ref, style, scheduleUpdate }) => (
            tag(Suggestions, {
              containerRef: ref,
              scheduleUpdate,
              style,
              suggestionsRef,
              value
            })
          )
        )
      ),
      !disabled && tag(EmojiPicker, {
        value,
        onChange
      })
    )
  )
}

EmojiMentionsInput.propTypes = {
  // Values
  autoCapitalize: PropTypes.string,
  autoComplete: PropTypes.string,
  autoCorrect: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  spellCheck: PropTypes.string,
  value: PropTypes.string,
  // Callbacks
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func
}

EmojiMentionsInput.defaultProps = {
  autoCapitalize: 'off',
  autoComplete: 'off',
  autoCorrect: 'off',
  popperPlacement: 'bottom',
  spellCheck: 'false'
}

module.exports = EmojiMentionsInput

// BEM Block entity Element components

const SUGGESTIONS_TIMEOUTS = [ 50, 100, 150, 200, 250, 300 ]

function Suggestions(props) {
  /**
   * `react-mentions` does not show the suggestions immediately;
   * aggressively try to re-position the popper after value changes.
   */
  React.useEffect(() => {
    let timeoutIndex = 0
    let timeoutId
    (function nextTimeout() {
      if (timeoutIndex > 0) {
        props.scheduleUpdate()
      }
      const timeoutDelay = SUGGESTIONS_TIMEOUTS[timeoutIndex++]
      if (typeof timeoutDelay === 'number') {
        timeoutId = setTimeout(nextTimeout, timeoutDelay)
      }
    })()
    return () => {
      clearTimeout(timeoutId)
    }
  }, [props.value])

  return (
    tag('div', {
      className: bem.element('suggestions-container'),
      ref: props.containerRef,
      style: props.style
    },
      tag('div', {
        ref: props.suggestionsRef
      })
    )
  )
}

Suggestions.propTypes = {
  containerRef: morePropTypes.domRef.isRequired,
  scheduleUpdate: PropTypes.func.isRequired,
  style: PropTypes.object.isRequired,
  suggestionsRef: morePropTypes.domRef.isRequired,
  value: PropTypes.string.isRequired
}

function EmojiPicker(props) {
  const [ isPickerVisible, setPickerVisible ] = React.useState(false)
  const togglePickerVisible = () => setPickerVisible(x => !x)
  const appendEmojiToValue = emoji => {
    props.onChange(null, null, props.value + emoji.native + ' ')
    setPickerVisible(false)
  }
  return (
    tag(ReactPopper.Manager,
      tag(ReactPopper.Reference,
        ({ ref }) => (
          tag(CircleButton, {
            className: bem.element('emoji-button'),
            forwardedRef: ref,
            title: 'Show Emoji Picker',
            onClick: togglePickerVisible
          })
        )
      ),
      isPickerVisible && tag(ReactPopper.Popper, {
        placement: 'bottom'
      },
        ({ ref, style }) => (
          tag('div', {
            className: bem.element('emoji-picker'),
            ref,
            style
          },
            tag(EmojiMart.Picker, {
              emoji: 'thinking_face',
              notFoundEmoji: 'mag_right',
              title: 'Pick an Emoji',
              onSelect: appendEmojiToValue
            })
          )
        )
      )
    )
  )
}

EmojiPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

// react-mentions emoji helpers

const EMOJI_MARKER_REGEXP = /:([0-9a-z+_-]+):/gi
const EMOJI_MENTIONS_LIMIT = 5
const EMOJI_DEFAULT_RESULTS = [
  '+1',
  'grinning',
  'kissing_heart',
  'heart_eyes',
  'laughing'
]

function replaceEmojiMarkup(inputText) {
  return inputText.replace(EMOJI_MARKER_REGEXP, (match, id) => {
    if (id) {
      const emoji = resolveEmojiById(id.toLowerCase())
      if (emoji) {
        return emoji.native
      }
    }
    return match
  })
}

function resolveEmojiById(id) {
  const emoji = EmojiMart.emojiIndex.emojis[id]
  if (emoji) {
    return emoji.id !== id ? emoji['1'] : emoji
  }
}

function findEmoji(query) {
  if (query) {
    return EmojiMart.emojiIndex.search(query)
  }
  return EMOJI_DEFAULT_RESULTS.map(resolveEmojiById)
}

function queryEmojiMentionData(query, callback) {
  const data = findEmoji(query)
  .map(emoji => ({ id: emoji.native, emoji }))
  .slice(0, EMOJI_MENTIONS_LIMIT)
  callback(data)
}

function renderEmojiSuggestion(entry, search, highlightedDisplay, index, focused) {
  const { emoji } = entry
  return (
    tag('div', {
      className: bem.element('emoji-suggestion', {
        active: focused
      })
    },
      tag('span', {
        className: bem.element('emoji-preview')
      },
        emoji.native
      ),
      tag('span', {
        className: bem.element('emoji-name')
      },
        emoji.id
      )
    )
  )
}
